import { FC, useEffect, useState } from "react";
import { useFetchSuppliers } from "../../../../apolloClient/hooks/itemCreation/fetchHooks";
import { ColDiv } from "../../../../components/misc/misc.styled";
import { ProjectProductMeterial } from "../../../../redux/types/Material";
import ShingleMaterialCard from "../../../../components/ui/ShingleMaterialCard";
import { useFetchProjectDetails } from "../../../../apolloClient/hooks/general/fetchHook";
import { MaterialTable } from "./MaterialTable";
import { useParams } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { UPDATE_ORDER_STATUS } from "../../../../apolloClient/operation/mutations/mutation";
import { ProjectProductSystemOrderStatusInput, ComponentType, OrderStatusEnum } from "../../../../redux/types/common";
import { toast } from "react-toastify";
import ConfirmationDialog from "../../../../components/dialogue/ConfirmationDialog";
import { logEvent, PROJECT_EVENTS } from "../../../../utils/amplitude";
import { Box } from "@mui/material";

interface MaterialProps {
  materials?: ProjectProductMeterial[]; // Optional prop
}

export const MaterialDetails: FC<MaterialProps> = ({ materials }) => {
  const [materialList, setMaterialList] = useState<ProjectProductMeterial[]>(
    materials || [] // Initialize with an empty array if materials is undefined
  );

  // State for confirmation dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingOrderChange, setPendingOrderChange] = useState<{
    productSystemId: number;
    statusType: 'orderSent' | 'orderConfirmed';
    value: boolean;
    currentOrderSent: boolean;
    currentOrderConfirmed: boolean;
    productSystemName?: string;
  } | null>(null);

  // State for supplier and job start date selection (for order confirmation flow)
  const [selectedSupplier, setSelectedSupplier] = useState<string>("");
  const [jobStartDate, setJobStartDate] = useState<string>("");
  const [selectedBrand, setSelectedBrand] = useState<string>("glas");

  // Helper function to format date for display
  const formatDateForDisplay = (date: string) => {
    const newDate = new Date(date);
    return newDate.toDateString();
  };

  // Fetch suppliers from API
  const { suppliers, loading: suppliersLoading } = useFetchSuppliers({ limit: 100, offSet: 0 });

  const deleteMaterial = (index: number) => {
    setMaterialList((prevMaterials) =>
      prevMaterials.filter((_, i) => i !== index)
    );
    console.log("Material deleted");
  };

  let { id } = useParams();

  console.log("id", id);

  const {
    projectDetails: projectDetailsData,
    loading: projectDetailsLoading,
    error: projectDetailsError,
    refetch,
  } = useFetchProjectDetails({
    projectId: Number(id),
  });


  const [updateProjectProductSystemOrderStatus, { loading: updateLoading }] = useMutation<
    { UpdateProjectProductSystemOrderStatus: boolean },
    { input: ProjectProductSystemOrderStatusInput }
  >(UPDATE_ORDER_STATUS);

  // Handle order status changes with confirmation
  const handleOrderStatusChange = async (
    productSystemId: number,
    statusType: 'orderSent' | 'orderConfirmed',
    value: boolean,
    currentOrderSent: boolean,
    currentOrderConfirmed: boolean,
    productSystemName?: string
  ) => {
    // Only show confirmation when checking the box (value === true)
    if (value) {
      setPendingOrderChange({
        productSystemId,
        statusType,
        value,
        currentOrderSent,
        currentOrderConfirmed,
        productSystemName,
      });
      setConfirmDialogOpen(true);
      // DO NOT update anything yet - wait for confirmation
    } else {
      // If unchecking, proceed without confirmation
      await executeOrderStatusChange(
        productSystemId,
        statusType,
        value,
        currentOrderSent,
        currentOrderConfirmed
      );
    }
  };

  // Handle changing order sent details (when order is already sent)
  const handleChangeOrderSent = (
    productSystemId: number,
    productSystemName?: string,
    existingSupplierId?: number,
    existingJobStartDate?: string
  ) => {
    // Pre-populate the form with existing values
    if (existingSupplierId && existingSupplierId !== 0) {
      setSelectedSupplier(existingSupplierId.toString());
    } else {
      setSelectedSupplier("");
    }

    if (existingJobStartDate) {
      // Convert from MM/DD/YYYY to YYYY-MM-DD for date input
      const parts = existingJobStartDate.split('/');
      if (parts.length === 3) {
        const [mm, dd, yyyy] = parts;
        setJobStartDate(`${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`);
      }
    } else {
      setJobStartDate("");
    }

    // Open dialog to change the supplier and job start date
    setPendingOrderChange({
      productSystemId,
      statusType: 'orderSent',
      value: true, // Keep it as sent
      currentOrderSent: true,
      currentOrderConfirmed: false,
      productSystemName,
    });
    setConfirmDialogOpen(true);
  };

  // Execute the actual order status change
  const executeOrderStatusChange = async (
    productSystemId: number,
    statusType: 'orderSent' | 'orderConfirmed',
    value: boolean,
    currentOrderSent: boolean,
    currentOrderConfirmed: boolean
  ) => {
    try {
      if (!productSystemId || productSystemId === 0) {
        toast("Invalid product system ID", { type: "error" });
        return;
      }

      // Prepare the mutation input (with supplierId and jobStartDate as MMDDYYYY)
      let formattedJobStartDate = undefined;
      if (jobStartDate) {
        // Parse YYYY-MM-DD directly to avoid UTC→local timezone shift.
        // new Date("YYYY-MM-DD") parses as UTC midnight, which shifts the date
        // back one day for users in negative UTC offsets (e.g. USA timezones).
        const [yyyy, mm, dd] = jobStartDate.split('-');
        formattedJobStartDate = `${mm}/${dd}/${yyyy}`;
      }

      // Determine the OrderStatus based on statusType and value
      let orderStatus: OrderStatusEnum;
      if (statusType === 'orderSent') {
        orderStatus = value ? OrderStatusEnum.SENT : OrderStatusEnum.NOT_SENT;
      } else { // statusType === 'orderConfirmed'
        orderStatus = value ? OrderStatusEnum.CONFIRMED : OrderStatusEnum.UNCONFIRMED;
      }

      const input: ProjectProductSystemOrderStatusInput = {
        projectId: Number(id),
        productSystemId: productSystemId,
        componentType: ComponentType.MATERIAL,
        orderStatus: orderStatus,
        supplierId: selectedSupplier ? Number(selectedSupplier) : undefined,
        jobStartDate: formattedJobStartDate,
        source: selectedBrand,
      };

      // Execute the mutation
      const { data } = await updateProjectProductSystemOrderStatus({
        variables: { input }
      });

      if (data?.UpdateProjectProductSystemOrderStatus) {
        toast(`Order ${statusType === 'orderSent' ? 'sent' : 'confirmed'} status updated!`, {
          type: "success"
        });

        // Track order status change
        logEvent(
          statusType === 'orderSent' ? PROJECT_EVENTS.MATERIAL_ORDER_SENT : PROJECT_EVENTS.MATERIAL_ORDER_CONFIRMED,
          {
            project_id: Number(id),
            product_system_id: productSystemId,
            status: value,
            supplier_id: selectedSupplier ? Number(selectedSupplier) : undefined,
            job_start_date: formattedJobStartDate || undefined,
            timestamp: new Date().toISOString(),
          }
        );

        // Refetch the project details to get updated data
        refetch();
      } else {
        toast("Failed to update order status", { type: "error" });
      }
    } catch (error) {
      console.error("Error updating order status:", error);
      toast("Error updating order status", { type: "error" });
    }
  };

  // Handle confirmation dialog confirm
  const handleConfirmOrderChange = async () => {
    if (pendingOrderChange) {
      await executeOrderStatusChange(
        pendingOrderChange.productSystemId,
        pendingOrderChange.statusType,
        pendingOrderChange.value,
        pendingOrderChange.currentOrderSent,
        pendingOrderChange.currentOrderConfirmed
      );
    }
    setConfirmDialogOpen(false);
    setPendingOrderChange(null);
    // Clear form values
    setSelectedSupplier("");
    setJobStartDate("");
  };

  // Handle confirmation dialog cancel
  const handleCancelOrderChange = () => {
    setConfirmDialogOpen(false);
    setPendingOrderChange(null);
    // Clear form values
    setSelectedSupplier("");
    setJobStartDate("");
    // No refetch needed - checkbox state is controlled by server data and won't change
  };

  // Get dialog title and message based on status type
  const getDialogContent = () => {
    if (!pendingOrderChange) return { title: "", message: "" };

    const { statusType, productSystemName } = pendingOrderChange;
    const systemName = productSystemName || "this product system";

    if (statusType === 'orderSent') {
      return {
        title: "Confirm Order Sent",
        message: `Are you sure you want to mark the order as sent for ${systemName}?`,
      };
    } else {
      return {
        title: "Confirm Order Received",
        message: `Are you sure you want to confirm that the order has been received for ${systemName}?`,
      };
    }
  };

  // Trigger the refetch when the id changes (if needed)
  useEffect(() => {
    if (id) {
      refetch(); // Call refetch to make sure the query is updated when the id changes
    }
  }, [id, refetch]);

  const materialMeasurements =
    projectDetailsData?.insuranceScope?.materialMeasurements;

  const dialogContent = getDialogContent();

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ width: "100%", marginBottom: 2 }}>
        {projectDetailsLoading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 200,
              color: "#6b7280",
            }}
          >
            Loading...
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {materialMeasurements && materialMeasurements.length === 0 ? (
              <Box
                sx={{
                  textAlign: "center",
                  padding: 6,
                  color: "#9ca3af",
                  fontSize: 14,
                }}
              >
                No data to display.
              </Box>
            ) : (
              <MaterialTable
                materialMeasurements={materialMeasurements}
                onOrderStatusChange={handleOrderStatusChange}
                onChangeOrderSent={handleChangeOrderSent}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDialogOpen}
        title={dialogContent.title}
        message={
          <Box>
            {/* Only show supplier and date inputs for Order Sent, not for Order Confirmed */}
            {pendingOrderChange?.statusType === 'orderSent' && (
              <>
                {/* Display Installation Date Range */}
                {projectDetailsData?.installationDetails?.fromDate && projectDetailsData?.installationDetails?.toDate && (
                  <Box
                    sx={{
                      marginBottom: 3,
                      padding: 2,
                      backgroundColor: '#f0f9ff',
                      borderRadius: 2,
                      border: '1px solid #bae6fd'
                    }}
                  >
                    <Box sx={{ fontWeight: 600, marginBottom: 1, fontSize: 14, color: '#0369a1' }}>
                      Installation Date Range (Customer Selected)
                    </Box>
                    <Box sx={{ fontSize: 14, color: '#0c4a6e' }}>
                      {formatDateForDisplay(projectDetailsData.installationDetails.fromDate)} - {formatDateForDisplay(projectDetailsData.installationDetails.toDate)}
                    </Box>
                  </Box>
                )}

                {/* Supplier Dropdown */}
                <Box sx={{ marginBottom: 2 }}>
                  <Box sx={{ fontWeight: 500, marginBottom: 1, fontSize: 14 }}>Supplier</Box>
                  <select
                    value={selectedSupplier}
                    onChange={e => setSelectedSupplier(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: 6,
                      border: '1px solid #e5e7eb',
                      fontSize: 14,
                    }}
                    disabled={suppliersLoading}
                  >
                    <option value="">{suppliersLoading ? "Loading suppliers..." : "Select a supplier"}</option>
                    {suppliers && suppliers.map(supplier => (
                      <option key={supplier.id} value={supplier.id.toString()}>{supplier.name}</option>
                    ))}
                  </select>
                </Box>
                {/* Job Start Date Input */}
                <Box sx={{ marginBottom: 2 }}>
                  <Box sx={{ fontWeight: 500, marginBottom: 1, fontSize: 14 }}>Job Start Date</Box>
                  <input
                    type="date"
                    value={jobStartDate}
                    onChange={e => setJobStartDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: 6,
                      border: '1px solid #e5e7eb',
                      fontSize: 14,
                    }}
                  />
                </Box>
              </>
            )}
            {/* Brand Styling Selection */}
            <Box sx={{ marginBottom: 2 }}>
              <Box sx={{ fontWeight: 500, marginBottom: 1, fontSize: 14 }}>Brand Styling (for vendor email)</Box>
              <select
                value={selectedBrand}
                onChange={e => setSelectedBrand(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 6,
                  border: '1px solid #e5e7eb',
                  fontSize: 14,
                  backgroundColor: '#fff',
                }}
              >
                <option value="glas">Glas</option>
                <option value="waybe">Waybe</option>
              </select>
            </Box>
            <Box sx={{ fontSize: 14, color: "#374151" }}>{dialogContent.message}</Box>
          </Box>
        }
        onConfirm={handleConfirmOrderChange}
        onCancel={handleCancelOrderChange}
        confirmText="Confirm"
        cancelText="Cancel"
      />
    </Box>
  );
};
